/**
 * State machine for one Insiders claim card (COM-506). UI-agnostic: it owns the
 * `Loyalty-FormData` read, the form values, the submit, the CSRF retry and the
 * double-submit guard; the organism only renders `status`.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import {
  fetchInsidersFormData,
  invalidateInsidersFormData,
  submitInsidersClaim,
} from "./insiders-claim.client";
import {
  buildClaimBody,
  getInitialClaimValues,
  isNotEnrolledError,
  mergeRedemptions,
  resolveActionUrl,
} from "./insiders-claim.logic";
import type {
  ClaimOutcome,
  ClaimStatus,
  ClaimValues,
  LoyaltyFormData,
  UseInsidersClaimOptions,
  UseInsidersClaimResult,
} from "./types";

const READY: ClaimStatus = { kind: "ready" };
const LOADING: ClaimStatus = { kind: "loading" };
const GUEST: ClaimStatus = { kind: "guest" };
const UNAVAILABLE: ClaimStatus = { kind: "unavailable" };
const NOT_ENROLLED: ClaimStatus = { kind: "notEnrolled" };

function statusForOutcome(outcome: ClaimOutcome): ClaimStatus {
  switch (outcome.kind) {
    case "success":
      return { kind: "succeeded", message: outcome.message };
    case "rejected":
      // Annex's raw not-enrolled reply is an internal string, never shopper
      // copy; every other rejection ("already claimed", the opted-out message)
      // is SFRA-authored and renders verbatim.
      return isNotEnrolledError(outcome.error)
        ? NOT_ENROLLED
        : { kind: "failed", message: outcome.error, format: "html" };
    case "serverError":
      return { kind: "failed", message: outcome.message, format: "text" };
    case "csrfExpired":
    case "malformed":
    case "network":
      return { kind: "failed", message: null, format: "text" };
  }
}

const canSubmit = (status: ClaimStatus): boolean =>
  status.kind === "ready" ||
  status.kind === "succeeded" ||
  status.kind === "failed";

export function useInsidersClaim({
  variant,
  siteId,
  enabled,
  isLoggedIn,
  isEnrolled,
}: UseInsidersClaimOptions): UseInsidersClaimResult {
  const [baseStatus, setStatus] = useState<ClaimStatus>(
    enabled ? LOADING : READY,
  );
  const [formData, setFormData] = useState<LoyaltyFormData | null>(null);
  const [values, setValues] = useState<ClaimValues>(() =>
    getInitialClaimValues(null),
  );
  // Bumped to re-read `Loyalty-FormData` (login flip). Kept out of the fetch
  // effect's deps list so a bump is the ONLY way to refetch.
  const [generation, setGeneration] = useState(0);
  // Same-tick double-click guard. `status` flips on the next commit, so a
  // second click in the same tick would otherwise see "ready" and post twice
  // (the `reorder-button.tsx` lesson).
  const inFlight = useRef(false);
  const lastLoggedIn = useRef(isLoggedIn);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    setStatus(LOADING);
    void fetchInsidersFormData(siteId).then(result => {
      if (cancelled) return;
      if (result.kind !== "ok") {
        setStatus(UNAVAILABLE);
        return;
      }
      setFormData(result.data);
      setValues(getInitialClaimValues(result.data.loyaltyData));
      setStatus(result.data.isAuthenticated ? READY : GUEST);
    });
    return () => {
      cancelled = true;
    };
  }, [enabled, siteId, generation]);

  // The account drawer signs in through a fetcher — no document reload — so
  // the Workers auth flip is the cue to re-read SFRA's view of the session.
  useEffect(() => {
    if (lastLoggedIn.current === isLoggedIn) return;
    lastLoggedIn.current = isLoggedIn;
    invalidateInsidersFormData(siteId);
    setGeneration(current => current + 1);
  }, [isLoggedIn, siteId]);

  const setAmount = useCallback((amount: string) => {
    setValues(current => ({ ...current, amount }));
  }, []);

  const setAnswer = useCallback((answer: boolean) => {
    setValues(current => ({ ...current, answer }));
  }, []);

  const submit = useCallback(async () => {
    if (inFlight.current || !formData || !canSubmit(baseStatus)) return;
    inFlight.current = true;
    setStatus({ kind: "submitting" });
    try {
      const attempt = (data: LoyaltyFormData) =>
        submitInsidersClaim(
          resolveActionUrl(data.actionUrl, siteId),
          buildClaimBody({ variant, csrf: data.csrf, values }),
        );

      let current = formData;
      let outcome = await attempt(current);

      if (outcome.kind === "csrfExpired") {
        // The token was minted at render and may be stale by click time. SFRA
        // also logs the SFRA session out on a CSRF failure, so re-read identity
        // and retry ONCE — never blindly.
        invalidateInsidersFormData(siteId);
        const refreshed = await fetchInsidersFormData(siteId);
        if (refreshed.kind !== "ok") {
          setStatus(UNAVAILABLE);
          return;
        }
        current = refreshed.data;
        setFormData(current);
        if (!current.isAuthenticated) {
          setStatus(GUEST);
          return;
        }
        outcome = await attempt(current);
      }

      if (outcome.kind === "success" || outcome.kind === "rejected") {
        const { redemptions } = outcome;
        setFormData(previous =>
          previous
            ? {
                ...previous,
                loyaltyData: mergeRedemptions(
                  previous.loyaltyData,
                  redemptions,
                ),
              }
            : previous,
        );
      }
      setStatus(statusForOutcome(outcome));
    } finally {
      inFlight.current = false;
    }
  }, [baseStatus, formData, siteId, values, variant]);

  // Derived, not stored: keeping `isEnrolled` out of the fetch effect's deps
  // preserves "a `generation` bump is the ONLY way to refetch", and lets the
  // card flip straight back to `ready` when a revalidation refreshes
  // `customerLoyalty` after the shopper enrolls elsewhere on the page.
  const status =
    baseStatus.kind === "ready" && !isEnrolled ? NOT_ENROLLED : baseStatus;

  return { status, values, setAmount, setAnswer, submit };
}
